import React from 'react';
import { useTranslation } from 'react-i18next';

import { SettingsIcon } from '@/shared/assets/icons/settings-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { DateTimeFormatForm } from '@/shared/features/header/components/date-time-format/date-time-format-form';
import {
  TDateTimeFormatInputSchema,
  TDateTimeFormatProps,
} from '@/shared/features/header/components/date-time-format/date-time-format.types';
import { dateTimeFormatInputSchema } from '@/shared/features/header/components/date-time-format/date-time-format.validation';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';
import { showToastError, showToastSuccess } from '@/shared/utils/toast';

export const DateTimeFormat: React.FC<TDateTimeFormatProps> = ({
  handleClose,
}) => {
  const { t } = useTranslation();
  const { dateTimeFormat, setDateTimeFormat } = useDateUtils();

  const getDefaultValues = () => {
    const dateFormat = dateTimeFormat.slice(0, 10);
    const timeFormat = dateTimeFormat.slice(11).trim();

    return { dateFormat, timeFormat };
  };

  const onSubmit = (
    data: TSubmittedData<TDateTimeFormatInputSchema>,
    handleClose: () => void,
  ) => {
    const _dateFormat =
      data.dirtyData.dateFormat ?? getDefaultValues().dateFormat;
    const _timeFormat =
      data.dirtyData.timeFormat ?? getDefaultValues().timeFormat;
    const combinedFormat = `${_dateFormat} ${_timeFormat}`;

    try {
      setDateTimeFormat(combinedFormat);
      showToastSuccess(t('ui:date_time_format.success_message'));
      handleClose();
    } catch {
      showToastError(t('ui:date_time_format.error_message'));
    }
  };

  return (
    <DrawerController
      title={t('ui:date_time_format.title')}
      trigger={
        <MenuItem icon={<SettingsIcon />}>
          {t('ui:date_time_format.label')}
        </MenuItem>
      }
      onClose={handleClose}
    >
      {(closeDrawer) => {
        const _handleClose = () => {
          closeDrawer();
          handleClose();
        };
        return (
          <Form<TDateTimeFormatInputSchema>
            onSubmit={(data) => onSubmit(data, _handleClose)}
            schema={dateTimeFormatInputSchema}
            defaultValues={getDefaultValues()}
          >
            {() => <DateTimeFormatForm handleClose={_handleClose} />}
          </Form>
        );
      }}
    </DrawerController>
  );
};
